import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "Rooms", href: "#rooms" },
  { label: "Gallery", href: "#gallery" },
  { label: "Explore", href: "#explore" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

const scrollToId = (id) => {
  const el = document.getElementById(id.replace("#", ""));
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/92 backdrop-blur-xl border-b border-[#E5E0D8]"
          : "bg-white/70 backdrop-blur-lg border-b border-[#E5E0D8]/60"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
          <div className="w-11 h-11 rounded-full bg-[#1A1A1A] flex items-center justify-center text-[#C8A97E] font-serif text-xl font-semibold">
            H
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-serif text-[18px] font-semibold text-[#1A1A1A]">
              Hitha Residency
            </span>
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#C8A97E] font-medium">
              Service Apartment
            </span>
          </div>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollToId(link.href)}
              className="text-[13px] font-medium text-[#1A1A1A]/75 hover:text-[#C8A97E] transition-colors tracking-wide relative group"
              data-testid={`nav-${link.label.toLowerCase()}`}
            >
              {link.label}
              <span className="absolute bottom-[-4px] left-0 right-0 h-[1px] bg-[#C8A97E] scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => scrollToId("#contact")}
            className="hidden md:inline-flex bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white rounded-full px-6 h-10 text-[13px] font-semibold tracking-wide"
            data-testid="nav-book-btn"
          >
            Book Now
          </Button>
          <button
            className="md:hidden w-10 h-10 rounded-full hover:bg-[#F9F7F3] flex items-center justify-center"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? (
              <X className="w-5 h-5 text-[#1A1A1A]" />
            ) : (
              <Menu className="w-5 h-5 text-[#1A1A1A]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-[#E5E0D8] bg-white/95 backdrop-blur-xl">
          <div className="flex flex-col px-5 py-3">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                onClick={() => {
                  scrollToId(link.href);
                  setMobileOpen(false);
                }}
                className="text-left py-3 border-b border-[#E5E0D8]/60 text-[15px] text-[#1A1A1A]"
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}
            <Button
              onClick={() => {
                scrollToId("#contact");
                setMobileOpen(false);
              }}
              className="mt-4 bg-[#1A1A1A] text-white rounded-full h-11"
              data-testid="mobile-nav-book-btn"
            >
              Book Now
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
